import { type Corner, type LogEntry } from "./types";

// Calculate closest corner based on position
export const getClosestCorner = (x: number, y: number): Corner => {
  const windowWidth = window.innerWidth;
  const windowHeight = window.innerHeight;

  // Simple center-based detection (CSS safe areas are handled in positioning)
  const centerX = windowWidth / 2;
  const centerY = windowHeight / 2;

  const isLeft = x < centerX;
  const isTop = y < centerY;

  if (isTop && isLeft) {
    return "top-left";
  }

  if (isTop && !isLeft) {
    return "top-right";
  }

  if (!isTop && isLeft) {
    return "bottom-left";
  }

  return "bottom-right";
};

// Memoized formatter to avoid recreating WeakSet on every call
export const formatLogValue = (value: unknown): string => {
  if (value === null) {
    return "null";
  }

  if (value === undefined) {
    return "undefined";
  }

  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  if (typeof value === "function") {
    return `[Function: ${value.name || "anonymous"}]`;
  }

  if (typeof value === "object") {
    try {
      // Handle circular references and other JSON issues
      return JSON.stringify(value, (_key: string, newValue: unknown) => newValue, 2);
    } catch {
      // Fallback for any remaining JSON.stringify issues
      try {
        return Object.prototype.toString.call(value);
      } catch {
        return "[Object - cannot stringify]";
      }
    }
  }

  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-base-to-string
  return String(value);
};

export const getLogCountByLevel = (logs: LogEntry[]) => {
  const counts = { error: 0, warn: 0, info: 0, log: 0, debug: 0 };
  logs.forEach((log) => {
    counts[log.level] += 1;
  });
  return counts;
};
